import { AssignLocation } from 'utils/useAssignLocation';
import { subject } from '@casl/ability';
import { Alert, AlertList } from '@oncarrot/turnip';
import { Can } from 'context/AbilityProvider/Can';
import { useAccount } from 'hooks/useAccount.am';
import { type CurrentUser, useCurrentUser } from 'hooks/useCurrentUser';
import { PageNotFound } from 'pages/PageNotFound/PageNotFound';
import { matchPath, Navigate, useMatch, useParams } from 'react-router';
import { CenteredPageLoadingCarrot } from 'turnip/LoadingCarrot/LoadingCarrot';
import { Offset } from 'turnip/Offset/Offset';
import { PageLayoutContained } from 'turnip/PageLayoutContained/PageLayoutContained';
import { PageTitle } from 'turnip/PageTitle/PageTitle';
import { PageWithAuthenticatedNavs } from 'turnip/PageWithAuthenticatedNavs/PageWithAuthenticatedNavs';
import { SupportLink } from 'turnip/SupportLink/SupportLink';
import { routes } from 'utils/routes';
import {
  agencyUrl,
  dashboardAccountUrl,
  dashboardUrl,
  goPath,
  loginUrl,
} from 'utils/carrotUrls';
import { useCurrentUrl } from 'utils/useCurrentUrl';

/**
 * If the user is logged out, redirect them to log in. If the user is logged
 * in, restrict access to the page based on their account status and
 * permissions.
 */
export function AuthorizedPage({ children }: { children: React.ReactNode }) {
  const { data } = useCurrentUser();
  const currentUrl = useCurrentUrl();

  if (!data?.user) {
    return (
      <>
        <AssignLocation url={loginUrl(currentUrl)} />
        <CenteredPageLoadingCarrot title="Redirecting to login page" />
      </>
    );
  }

  return (
    <RedirectOptedInToDashboard user={data.user}>
      <RestrictByAccountAccess>
        <RestrictByAccountStatus user={data.user}>
          <RestrictToBillingAdmins>
            <RestrictBySiteAccess>
              <RestrictBySiteSettingsAccess>
                {children}
              </RestrictBySiteSettingsAccess>
            </RestrictBySiteAccess>
          </RestrictToBillingAdmins>
        </RestrictByAccountStatus>
      </RestrictByAccountAccess>
    </RedirectOptedInToDashboard>
  );
}

/**
 * Members who have opted into the new dashboard (by landing on
 * dashboard.carrot; account-manager is the source of truth and mirrors the
 * state here via the `dashboardOptInState` field) should experience the
 * dashboard rather than this legacy my.carrot app. Redirect them there,
 * mapping each my.carrot route to its dashboard equivalent where one exists
 * and falling back to the dashboard root otherwise.
 */
function RedirectOptedInToDashboard({
  user,
  children,
}: {
  user: CurrentUser;
  children: React.ReactNode;
}) {
  const destination = useDashboardDestination(user);

  if (!destination) {
    return children;
  }

  return (
    <>
      <AssignLocation url={destination} />
      <CenteredPageLoadingCarrot title="Redirecting to your dashboard" />
    </>
  );
}

/**
 * Resolve the dashboard URL an opted-in member on the current my.carrot route
 * should be redirected to, mapping each route to its dashboard equivalent and
 * falling back to the dashboard root when no mapping applies. Returns `null`
 * when the member has not opted in.
 */
function useDashboardDestination({
  dashboardOptInState,
}: CurrentUser): string | null {
  const { accountId, siteId } = useParams();

  const accountCallTrackingMatch = useMatch({
    path: '/account/:accountId/call-tracking/*',
  });
  const legacyCallTrackingMatch = useMatch({
    path: '/account/call-tracking/*',
  });
  const siteMatch = useMatch('/account/:accountId/site/:siteId');
  const agencyMatch = useMatch({ path: '/account/:accountId/agency/*' });
  const onboardingMatch = useMatch({
    path: '/account/:accountId/onboarding/*',
  });
  const accountTeamMatch = useMatch({
    path: '/account/:accountId/team-members/*',
  });
  const billingCancelMatch = useMatch('/account/billing/cancel');
  const billingEditMatch = useMatch('/account/billing/edit');
  const billingMatch = useMatch({ path: '/account/billing/*' });
  const legacyTeamMatch = useMatch({ path: '/account/team-members/*' });
  const keywordExplorerMatch = useMatch('/keyword-explorer');

  if (dashboardOptInState !== 'opted_in') {
    return null;
  }

  // Routes without an `:accountId` in the URL (billing, team, keyword explorer,
  // Call Tracking) are account-agnostic here but live under an account on the
  // dashboard. `me` is an account-manager deep-link sentinel: account-manager
  // resolves it to the member's account, or shows its account picker when it
  // can't choose one.
  const deepLinkAccountId = 'me';
  const agnosticAccountUrl = dashboardAccountUrl(deepLinkAccountId);
  const billingUrl = `${agnosticAccountUrl}/settings/billing`;

  const callTrackingMatch = accountCallTrackingMatch ?? legacyCallTrackingMatch;
  if (callTrackingMatch) {
    const callTrackingUrl = `${dashboardAccountUrl(accountId ?? deepLinkAccountId)}/call-tracking`;
    // WordPress (the `app` repo) links members to a number's edit page; every
    // other Call Tracking page lands on the dashboard's Call Tracking index.
    const numberEditMatch = matchPath(
      '/numbers/edit/:numberId',
      `/${callTrackingMatch.params['*']}`
    );

    return numberEditMatch
      ? `${callTrackingUrl}/numbers/${numberEditMatch.params.numberId}/edit`
      : callTrackingUrl;
  }

  if (siteMatch && accountId && siteId) {
    return `${dashboardAccountUrl(accountId)}/sites/wp/${siteId}`;
  }

  if (agencyMatch) {
    return agencyUrl;
  }

  if (onboardingMatch && accountId) {
    return `${dashboardAccountUrl(accountId)}/onboarding`;
  }

  if (accountTeamMatch && accountId) {
    return `${dashboardAccountUrl(accountId)}/settings/team`;
  }

  if (billingCancelMatch) {
    return `${billingUrl}/cancel`;
  }

  if (billingEditMatch) {
    return `${billingUrl}/edit`;
  }

  if (billingMatch) {
    return billingUrl;
  }

  if (legacyTeamMatch) {
    return `${agnosticAccountUrl}/settings/team`;
  }

  if (keywordExplorerMatch) {
    return `${agnosticAccountUrl}/seo/keywords`;
  }

  if (accountId) {
    return dashboardAccountUrl(accountId);
  }

  return dashboardUrl;
}

function RestrictByAccountAccess({ children }: { children: React.ReactNode }) {
  // TODO: ALUM - remove switch account route checks when the route no longer exists
  const switchAccountRouteMatch = useMatch({
    path: '/account/switch/:accountId',
    end: false,
  });
  const { accountId } = useParams();
  if (!accountId || switchAccountRouteMatch) {
    return children;
  }

  const account = subject('Account', {
    id: parseInt(accountId),
  });

  return (
    <>
      <Can I="read" this={account}>
        {children}
      </Can>

      <Can not I="read" this={account}>
        <PageWithAuthenticatedNavs>
          <PageNotFound />
        </PageWithAuthenticatedNavs>
      </Can>
    </>
  );
}

/**
 * Render the provided children if the active account has the necessary account status.
 */
function RestrictByAccountStatus({
  user: { superAdmin },
  children,
}: {
  user: CurrentUser;
  children: React.ReactNode;
}) {
  const accountQuery = useAccount();
  // Accounts without an active subscription have restricted access.
  const carrotAccountStatus =
    accountQuery.data?.account?.owner.carrotAccountStatus;
  const userHasAllowedStatus = !carrotAccountStatus
    ? false
    : ['active', 'active_past_due', 'trialing'].includes(carrotAccountStatus);

  /**
   * Certain routes are exceptions that should be accessible to all users,
   * however, regardless of if they have an allowed status.
   *
   * The billing route is allowed because a) all current and former users
   * should be able to access their old invoices, etc., and b) we send users
   * who need to reactivate or resolve other account problems there.
   *
   * The onboarding pages are allowed because a) it should be harmless, and b)
   * sometimes subscription data hasn't been synced yet after sign-up
   * (typically for bundle sign-ups). This caused a bug where the user was
   * redirected to the billing home page instead of being sent to the
   * onboarding flow.
   *
   * The switch account route is allowed so that users can change their active
   * account even if their current active account doesn't have an allowed
   * status.
   */
  const billingRouteMatch = useMatch(routes().billingHome);
  const oldOnboardingRouteMatch = useMatch({
    path: '/onboarding/',
    end: false,
  });
  const onboardingRouteMatch = useMatch({
    path: '/account/:accountId/onboarding/',
    end: false,
  });
  const accountAgnosticHomeRouteMatch = useMatch(routes().home);
  const accountRouteMatch = useMatch('/account/:accountId');
  const siteRouteMatch = useMatch('/account/:accountId/site/:siteId');
  // TODO: ALUM - remove switch account route checks when the route no longer exists
  const switchAccountRouteMatch = useMatch({
    path: '/account/switch/:accountId',
    end: false,
  });
  const goRouteMatch = useMatch(goPath());
  const indexRouteMatch = useMatch('/');
  const onExceptionRoute =
    accountAgnosticHomeRouteMatch ||
    accountRouteMatch ||
    billingRouteMatch ||
    goRouteMatch ||
    indexRouteMatch ||
    oldOnboardingRouteMatch ||
    onboardingRouteMatch ||
    siteRouteMatch ||
    switchAccountRouteMatch;

  // Redirect users who lack an allowed account status and are not accessing
  // one of the exception routes to the billing page.
  if (!superAdmin && !userHasAllowedStatus && !onExceptionRoute) {
    return <Navigate to={routes().billingHome} />;
  }

  return children;
}

/**
 * Render the provided children if the user has permission to edit the
 * subscription, or it's not a route that requires that permission.
 */
function RestrictToBillingAdmins({ children }: { children: React.ReactNode }) {
  // Check if the user is on a route that requires permission to make
  // billing/subscription changes
  const billingRouteMatch = useMatch({
    path: '/account/billing/*',
  });
  const routeRequiresBillingAbility =
    billingRouteMatch && billingRouteMatch.pathname !== routes().billingHome;

  if (!routeRequiresBillingAbility) {
    return children;
  }

  // NOTE: further into ALUM we may need to switch to checking a different ability
  return (
    <>
      <Can I="read" a="membership_editor">
        {children}
      </Can>

      <Can not I="read" a="membership_editor">
        <PageWithAuthenticatedNavs>
          <PageLayoutContained>
            <PageTitle>You are not authorized to access this page</PageTitle>

            <Offset top={4}>
              <Alert variant="error" headline="Not authorized">
                <p>You do not have permission to access this page.</p>

                <Offset top={2}>
                  <AlertList>
                    <li>
                      If you have been invited to another user&apos;s account,
                      you may need to ask the account owner to check your
                      permissions.
                    </li>
                    <li>
                      If you believe this is an error, please <SupportLink />.
                    </li>
                  </AlertList>
                </Offset>
              </Alert>
            </Offset>
          </PageLayoutContained>
        </PageWithAuthenticatedNavs>
      </Can>
    </>
  );
}

function RestrictBySiteAccess({ children }: { children: React.ReactNode }) {
  const { siteId } = useParams();
  if (!siteId) {
    return children;
  }

  const site = subject('Site', {
    platform_site_id: parseInt(siteId),
  });

  return (
    <>
      <Can I="read" this={site}>
        {children}
      </Can>

      <Can not I="read" this={site}>
        <PageWithAuthenticatedNavs disableSingleSiteNav={true}>
          <PageNotFound />
        </PageWithAuthenticatedNavs>
      </Can>
    </>
  );
}

function RestrictBySiteSettingsAccess({
  children,
}: {
  children: React.ReactNode;
}) {
  const { siteId } = useParams();
  const siteSettingsRouteMatch = useMatch({
    path: '/account/site/:siteId/settings/*',
  });

  if (!siteSettingsRouteMatch || !siteId) {
    return children;
  }

  const site = subject('Site', {
    platform_site_id: parseInt(siteId),
  });

  return (
    <>
      <Can I="update" this={site}>
        {children}
      </Can>

      <Can not I="update" this={site}>
        <PageWithAuthenticatedNavs>
          <PageNotFound />
        </PageWithAuthenticatedNavs>
      </Can>
    </>
  );
}
